import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Tarjeta de opción seleccionable · elegir entre pocas formas de arrancar algo.
 *
 * Estaba escrita a mano en el alta de prospecto (dos veces, con dos lenguajes
 * distintos: una con tokens y otra con `border-white/10 bg-white/[0.02]`, que
 * no responde al tema). Es un rol, no una combinación de clases.
 *
 * 🔴 El levantamiento de 1px al pasar el puntero es UNA de las dos excepciones
 * sancionadas a «la presión no desplaza» —la otra es la presión del botón—, y
 * vive acá, en el rol. Va con `motion-safe:` para que desaparezca con
 * movimiento reducido. El presupuesto de `hover:-translate` está en 1: si sube,
 * alguien lo escribió en una pantalla.
 *
 * La etiqueta «Recomendado» va en capitalización natural y NO en mayúscula,
 * aunque el estudio la dibuje así: las mayúsculas vienen del dato, nunca del
 * estilo. Es el único acento cálido fuera de Sol y del rol «Agente».
 */
interface OptionCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  /** Marca la tarjeta como elegida: borde y fondo de marca, ícono con gradiente. */
  selected?: boolean;
  /** Muestra la etiqueta cálida «Recomendado». Solo para lo asistido por IA. */
  recommended?: boolean;
  onSelect: () => void;
  className?: string;
}

export function OptionCard({
  icon,
  title,
  description,
  selected = false,
  recommended = false,
  onSelect,
  className,
}: OptionCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-selected={selected}
      aria-pressed={selected}
      className={cn(
        'dd-option flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-all duration-base',
        'motion-safe:hover:-translate-y-px',
        selected
          ? 'border-brand/55 bg-brand/[0.07]'
          : 'border-border bg-surface-lo/55 hover:border-border-strong',
        className,
      )}
    >
      <span
        className={cn(
          'dd-option-icon grid size-[34px] shrink-0 place-items-center rounded-md transition-colors',
          selected ? 'text-brand-hi' : 'bg-fg/7 text-fg-muted',
        )}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-foreground flex items-center gap-2 text-body-lg font-semibold">
          {title}
          {recommended && (
            <span className="bg-warm-soft text-warm rounded-full px-1.5 py-px text-micro leading-none font-semibold">
              Recomendado
            </span>
          )}
        </span>
        <span className="text-fg-muted mt-1 block text-caption leading-snug">
          {description}
        </span>
      </span>
    </button>
  );
}
